import { useQuery } from "@tanstack/react-query";

// Catalogus-API: leest rechtstreeks uit de eigen database (toptex_products e.d.).
// Prijzen en voorraad worden bewust NIET opgehaald of getoond — deze site is een
// catalogus met offerteaanvraag, geen webshop.
const FN_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toptex-catalog`;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function fetchToptex<T>(path: string): Promise<T> {
  const res = await fetch(`${FN_BASE}${path}`, {
    headers: { apikey: ANON, Authorization: `Bearer ${ANON}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Catalogus ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
}

export const IMAGE_WIDTHS = [300, 800, 1600] as const;

const FN_IMAGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/toptex-image`;
const CDN_IMAGE_BASE = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/product-images`;

export function catalogImageBase(reference: string, colorCode: string): string {
  return `${FN_IMAGE_BASE}/${encodeURIComponent(reference)}/${encodeURIComponent(colorCode)}`;
}

/** Rechtstreekse CDN-URL voor reeds omgezette WebP-varianten. */
function cdnBase(base: string): string {
  return base.startsWith(FN_IMAGE_BASE) ? CDN_IMAGE_BASE + base.slice(FN_IMAGE_BASE.length) : base;
}

/** Bouwt een srcset voor het lokale WebP-endpoint (geen TopTex CDN-URL's). */
export function imageSrcSet(base: string | null | undefined): string | undefined {
  if (!base) return undefined;
  const b = cdnBase(base);
  return IMAGE_WIDTHS.map((w) => `${b}/${w}.webp ${w}w`).join(", ");
}

export function imageSrc(base: string | null | undefined, width: 300 | 800 | 1600 = 800): string | undefined {
  if (!base) return undefined;
  return `${cdnBase(base)}/${width}.webp`;
}

/**
 * Nog niet omgezette foto's bestaan niet in de bucket: val terug op de edge
 * function, die de WebP on-demand genereert en opslaat.
 */
export function imageFallback(e: React.SyntheticEvent<HTMLImageElement>) {
  const img = e.currentTarget;
  if (img.dataset.fallback === "1") return;
  img.dataset.fallback = "1";
  const src = img.currentSrc || img.src;
  if (!src.startsWith(CDN_IMAGE_BASE)) return;
  img.removeAttribute("srcset");
  img.src = FN_IMAGE_BASE + src.slice(CDN_IMAGE_BASE.length);
}

// ---------- Genormaliseerde types (spiegelen de edge function) ----------
export interface NormColor {
  code: string;
  name: string;
  hex: string | null;
  imageBase: string | null;
  image: string | null;
  sizes: string[];
}
export interface NormProduct {
  ref: string;
  brand: string;
  name: string;
  type: string;
  family: string;
  description: string;
  salesArguments: string[];
  composition: string;
  weight: string;
  imageBase: string | null;
  image: string | null;
  colors: NormColor[];
}

export interface BrandSummary {
  id: string;
  name: string;
  count: number;
}

export interface ProductListResponse {
  items: NormProduct[];
  page: number;
  page_size: number;
  total_count: number;
  brands?: BrandSummary[];
}

export interface ProductFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  category?: string;
  brand?: string; // komma-gescheiden brand_id's (lowercase slug)
  gender?: string; // "Heren" | "Dames" | "Kind" | "Baby" | "Unisex"
  sort?: "name" | "brand" | "newest";
  enabled?: boolean;
}

export function useToptexProducts(filters: ProductFilters = {}) {
  const {
    page = 1,
    pageSize = 24,
    search = "",
    category = "",
    brand = "",
    gender = "",
    sort = "name",
    enabled = true,
  } = filters;
  return useQuery({
    queryKey: ["toptex-products", page, pageSize, search, category, brand, gender, sort],
    queryFn: () => {
      const qs = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort });
      if (search) qs.set("search", search);
      if (category) qs.set("category", category);
      if (brand) qs.set("brand", brand);
      if (gender) qs.set("gender", gender);
      return fetchToptex<ProductListResponse>(`/products?${qs}`);
    },
    staleTime: 30 * 60 * 1000,
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useToptexProduct(reference: string | undefined) {
  return useQuery({
    queryKey: ["toptex-product", reference],
    queryFn: () => fetchToptex<{ product: NormProduct | null }>(`/product/${encodeURIComponent(reference!)}`),
    enabled: !!reference,
    staleTime: 60 * 60 * 1000,
  });
}
